import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CKEditorComponent, ChangeEvent } from '@ckeditor/ckeditor5-angular';
import { ClassicEditor } from 'ckeditor5';
import { DEFAULT_FONT, EDITOR_CONFIG } from './editor-config';

function applyFontToAll(writer: any, node: any, fontFamily: string): boolean {
  let changed = false;
  for (const child of node.getChildren()) {
    if (child.is('$text')) {
      if (child.getAttribute('fontFamily') !== fontFamily) {
        writer.setAttribute('fontFamily', fontFamily, child);
        changed = true;
      }
    } else if (child.is('element')) {
      if (applyFontToAll(writer, child, fontFamily)) changed = true;
    }
  }
  return changed;
}

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class EditorComponent implements ControlValueAccessor, OnChanges {
  @ViewChild(CKEditorComponent) editorComponent!: CKEditorComponent;

  @Output()
  cambioValoreEditor = new EventEmitter();

  /**
   * Serve per rimuovere/disabilitare uno o più plugin dell'editor.
   * I plugin inclusi (e quindi disattivabili alla bisogna) sono visibili in ckeditor.ts
   */
  private _removePlugins: string[] = [];

  @Input()
  set removePlugins(plugins: string[]) {
    this._removePlugins = plugins || [];
  }

  get removePlugins() {
    return this._removePlugins;
  }

  public isLayoutReady = false;

  public Editor = ClassicEditor;

  public config = {
    ...EDITOR_CONFIG,
    removePlugins: this._removePlugins,
  }; // CKEditor needs the DOM tree before calculating the configuration.

  value = '';
  disabled = false;
  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnChanges() {
    this.config = {
      ...EDITOR_CONFIG,
      removePlugins: this._removePlugins ?? [],
    };
  }

  writeValue(value: any): void {
    if (this.value !== value) {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  changed(event: ChangeEvent) {
    const val = this.editorComponent.editorInstance?.data.get();
    this.onChange(val);
    this.cambioValoreEditor.emit(val);
  }

  onEditorReady(editor: any) {
    editor?.editorInstance?.execute('alignment', { value: 'justify' });

    const editorInstance = editor?.editorInstance ?? editor;
    if (!editorInstance?.model || !editorInstance?.plugins?.has('FontFamily')) return;

    editorInstance.model.document.registerPostFixer((writer: any) => {
      const root = editorInstance.model.document.getRoot();
      return applyFontToAll(writer, root, DEFAULT_FONT);
    });
  }
}
